import React, { useState, useEffect, useRef } from "react";
import { Card, Snackbar, Alert, Box, Typography } from "@mui/material";
import { CheckCircle, Warning, Error as ErrorIcon, Info } from "@mui/icons-material";

import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import HeaderPropiedades from "./components/HeaderPropiedades";
import TablePropiedades from "./components/TablePropiedades/TablePropiedades";
import { usePropiedades } from "./hooks/usePropiedades";
import logoImage from "../../../assets/images/logo horinzontal.png";

const Propiedades = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    autoHideDuration: 6000,
  });

  const tableRef = useRef(null);

  const {
    propiedades,
    loading: loadingPropiedades,
    error: errorPropiedades,
    stats,
    commerces,
    currentCommerceName,
    hasPermission,
    permisosPropiedades,
    addPropiedad,
    editPropiedad,
    togglePropiedadStatus,
    refreshData: refreshPropiedades,
    changeCommerce,
    hasData,
  } = usePropiedades();

  const isFirstLoad = useRef(true);
  const hasShownInitialMessage = useRef(false);

  const showNotification = (message, severity = "success", duration = 6000) => {
    setSnackbar({ open: true, message, severity, autoHideDuration: duration });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleRefresh = async () => {
    try {
      showNotification("Actualizando datos...", "info", 2000);
      await refreshPropiedades();
      showNotification("Datos actualizados correctamente", "success");
    } catch (err) {
      showNotification(`Error al actualizar: ${err.message}`, "error");
    }
  };

  const handleAddClick = () => {
    if (tableRef.current) {
      tableRef.current.openAddModal();
    }
  };

  const handleCommerceChange = async (commerceId, commerceName) => {
    try {
      showNotification(`Cambiando a ${commerceName}...`, "info", 2000);
      await changeCommerce(commerceId, commerceName);
      showNotification(`Propiedad cambiada a ${commerceName}`, "success");
    } catch (error) {
      showNotification(`Error al cambiar propiedad: ${error.message}`, "error");
    }
  };

  const handleAddPropiedad = async (newPropiedad) => {
    try {
      const result = await addPropiedad(newPropiedad);
      if (result && result.success) {
        showNotification("Propiedad agregada exitosamente", "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al agregar propiedad";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleAddPropiedad:", error);
      showNotification("Error al agregar propiedad", "error");
      return { success: false, error: error.message };
    }
  };

  const handleEditPropiedad = async (updatedPropiedad) => {
    try {
      const result = await editPropiedad(updatedPropiedad);
      if (result && result.success) {
        showNotification("Propiedad actualizada exitosamente", "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al actualizar propiedad";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleEditPropiedad:", error);
      showNotification("Error al actualizar propiedad", "error");
      return { success: false, error: error.message };
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const result = await togglePropiedadStatus(id, currentStatus);
      if (result && result.success) {
        showNotification(result.message, "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al cambiar estado";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleToggleStatus:", error);
      showNotification("Error al cambiar estado", "error");
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    if (errorPropiedades) {
      showNotification(errorPropiedades, "error", 8000);
    }
  }, [errorPropiedades]);

  useEffect(() => {
    if (
      isFirstLoad.current &&
      !loadingPropiedades &&
      hasData &&
      !errorPropiedades &&
      !hasShownInitialMessage.current
    ) {
      hasShownInitialMessage.current = true;
      showNotification(`Cargadas ${propiedades.length} propiedades`, "info", 5000);
    }
  }, [loadingPropiedades, hasData, errorPropiedades, propiedades.length]);

  useEffect(() => {
    if (isFirstLoad.current && !loadingPropiedades) {
      isFirstLoad.current = false;
    }
  }, [loadingPropiedades]);

  // Si no tiene permisos, mostrar mensaje con logo
  if (!hasPermission && !loadingPropiedades) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="GoPass"
              sx={{
                height: 80,
                width: "auto",
                objectFit: "contain",
                mb: 3,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Typography variant="h5" fontWeight="bold" color="text.secondary" gutterBottom>
              No tiene permisos para ver este módulo
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Contacte al administrador del sistema para solicitar acceso.
            </Typography>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ width: "100%", maxWidth: "500px", borderRadius: "8px" }}
          iconMapping={{
            success: <CheckCircle fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />,
            warning: <Warning fontSize="inherit" />,
            info: <Info fontSize="inherit" />,
          }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {snackbar.message}
          </MDTypography>
        </Alert>
      </Snackbar>

      <HeaderPropiedades
        onRefresh={handleRefresh}
        loading={loadingPropiedades}
        stats={stats}
        onAddClick={handleAddClick}
        hasCreatePermission={permisosPropiedades?.crear || false}
      />

      <MDBox py={3}>
        <Card
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
            border: errorPropiedades ? "1px solid #f44336" : "1px solid #e0e0e0",
          }}
        >
          <MDBox p={3} bgcolor="background.paper">
            <TablePropiedades
              ref={tableRef}
              propiedades={propiedades}
              loading={loadingPropiedades}
              error={errorPropiedades}
              onRefresh={refreshPropiedades}
              onAddPropiedad={handleAddPropiedad}
              onEditPropiedad={handleEditPropiedad}
              onToggleStatus={handleToggleStatus}
              // 🔥 Pasar los permisos con valores por defecto false
              hasCreatePermission={permisosPropiedades?.crear || false}
              hasEditPermission={permisosPropiedades?.editar || false}
              hasActivatePermission={permisosPropiedades?.activar || false}
              hasDeactivatePermission={permisosPropiedades?.desactivar || false}
            />
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default Propiedades;
